import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Input,
  Portal,
  Text,
  InputGroup,
  IconButton,
} from '@chakra-ui/react';
import { ChevronDownIcon, CloseIcon } from '@chakra-ui/icons';
import { ProgressBar } from './ProgressBar';

const DropdownMenu = ({
  row,
  onSelect,
}: {
  row: any;
  onSelect?: (id: number) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    if (!row.options || !searchTerm) return row.options || [];
    const lowerSearch = searchTerm.toLowerCase();
    return row.options.filter((o: any) =>
      o.label.toLowerCase().includes(lowerSearch),
    );
  }, [row.options, searchTerm]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setSearchTerm('');
    inputRef.current?.focus();
  };

  return (
    <Menu
      isLazy
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
    >
      <MenuButton
        as={Button}
        variant="link"
        size="sm"
        color="black"
        rightIcon={<ChevronDownIcon fontSize={{ base: '14px', hxl: '16px' }} />}
        fontWeight="800"
        fontSize={{ base: '16px', hxl: '18px' }}
        h="auto"
        py={0}
        px={0}
        m={0}
        display="flex"
        alignItems="center"
        textAlign="left"
        _hover={{ textDecoration: 'none' }}
        _active={{ bg: 'transparent' }}
      >
        {row.label || 'Select Branch'}
      </MenuButton>
      <Portal>
        <MenuList
          borderRadius="lg"
          p={2}
          zIndex={999}
          shadow="lg"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <InputGroup size="sm" mb={2}>
            <Input
              ref={inputRef}
              placeholder="Search Branches..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                }
              }}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              _focus={{
                borderColor: 'brand.400',
                boxShadow: '0 0 0 1px var(--chakra-colors-brand-400)',
              }}
            />
            {searchTerm && (
              <IconButton
                aria-label="Clear search"
                icon={<CloseIcon />}
                size="sm"
                position="absolute"
                right="4px"
                top="50%"
                transform="translateY(-50%)"
                onClick={handleClear}
                variant="ghost"
                h="auto"
                minW="auto"
                w="16px"
                p={0}
                _hover={{ bg: 'transparent' }}
                zIndex={1}
              />
            )}
          </InputGroup>
          <Box maxH="150px" overflowY="auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((o: any) => (
                <MenuItem
                  key={o.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect?.(o.id);
                    setIsOpen(false);
                  }}
                  fontSize="sm"
                >
                  {o.label}
                </MenuItem>
              ))
            ) : (
              <Box px={2} py={1} fontSize="sm" color="gray.500">
                No branches found
              </Box>
            )}
          </Box>
        </MenuList>
      </Portal>
    </Menu>
  );
};

export const DynamicContestWidget = ({ rows }: any) => {
  return (
    <VStack spacing={0} align="stretch" w="full">
      {rows.map((row: any, idx: number) => {
        const hasMultipleOptions = row.options && row.options.length > 1;

        const displayLabel = hasMultipleOptions ? (
          <DropdownMenu row={row} onSelect={row.onSelect} />
        ) : (
          <Text
            fontWeight="500"
            fontSize={{ base: '14px', hxl: '16px' }}
            color="black"
            p={0}
            m={0}
          >
            {row.label}
          </Text>
        );

        return (
          <ProgressBar
            key={idx}
            label={displayLabel}
            percentage={row.percentage}
            color={row.color}
          />
        );
      })}
    </VStack>
  );
};
